<template>
  <div class="portal-app-page">
    <RetailerHeader />
    <main class="portal-main portal-container">
    <PortalPageHeader
      v-if="!pendingApproval"
      eyebrow="Wholesale catalog"
      title="Partner shop"
      description="Choose a product and pack size below. All prices and availability come directly from your live partner catalog."
    >
      <template #actions><button class="uk-button uk-button-default" type="button" @click="openCart"><span uk-icon="icon: cart"></span> View cart · {{ cartCount }} kits</button></template>
    </PortalPageHeader>

    <div v-if="loading" class="uk-text-center uk-margin-large">
      <span uk-spinner="ratio: 2"></span>
    </div>

    <div v-else-if="pendingApproval" class="portal-empty-state ucari-retailer-card uk-text-center">
      <div class="uk-container uk-container-xsmall">
        <span uk-icon="icon: lock; ratio: 3" class="uk-text-muted"></span>
        <h2 class="uk-margin-top">Account Pending Approval</h2>
        <p class="uk-text-lead uk-text-muted">
          Your account is currently under review. You will be able to access the shop once your partner account has been approved.
        </p>
        <p>
          If you believe this is an error, please contact us at
          <a href="mailto:care@ucari.com">care@ucari.com</a>.
        </p>
      </div>
    </div>

    <div v-else-if="error" class="uk-alert-danger" uk-alert>
      {{ error }}
    </div>

    <div v-else class="shop-grid">
      <div v-for="p in products" :key="p.id">
        <article class="shop-card">
          <div class="shop-card__media">
            <span class="product-pill">Partner pricing</span>
            <img v-if="p.imageUrl" :src="p.imageUrl" :alt="p.name" class="product-img" />
            <div v-else class="shop-card__placeholder" uk-icon="icon: image; ratio: 2"></div>
          </div>
          <div class="shop-card__body">
            <h3>{{ p.name }}</h3>
            <p>{{ p.description }}</p>
            <div class="shop-card__price"><strong>${{ displayPrice(p) }}</strong><span>selected pack</span></div>
            <div v-if="p.variations.length > 1" class="uk-margin-small">
              <div class="variation-list">
                <div
                  class="variation-option"
                  v-for="v in p.variations"
                  :key="v.id"
                >
                  <label>
                    <input
                      class="uk-radio"
                      type="radio"
                      :value="v.id"
                      v-model="p.selectedVariationId"
                    />
                    <span>
                      <strong>{{ v.name }}</strong>
                      <small>{{ v.priceMoney?.currency }} ${{ formatMoney(v.priceMoney?.amount) }} · ${{ perKitPrice(v) }} per kit</small>
                    </span>
                  </label>
                </div>
              </div>
            </div>

          </div>

          <div class="shop-card__footer">
              <div class="qty-controls" aria-label="Quantity">
                <button class="uk-button uk-button-default uk-button-small" type="button" aria-label="Decrease quantity" @click="decrementQty(p)">−</button>
                <input 
                  class="uk-input uk-form-width-xsmall uk-text-center" 
                  type="number" 
                  min="1" 
                  max="9999" 
                  step="1"
                  pattern="[0-9]*"
                  v-model.number="p.qty"
                  @input="sanitizeQty(p)"
                  @blur="sanitizeQty(p)"                  
                />
                <button class="uk-button uk-button-default uk-button-small" type="button" aria-label="Increase quantity" @click="incrementQty(p)">+</button>
              </div>

            <button 
              class="uk-button uk-button-primary uk-button-small"
              :disabled="cartCount >= MAX_CART_KITS"
              @click="addToCart(p)">
              Add to cart
            </button>
          </div>
        </article>
      </div>
    </div>
    </main>
    <RetailerFooter />
  </div>
</template>

<script>
/* global UIkit */
import axios from 'axios';
import RetailerHeader from "./components/RetailerHeader.vue";
import RetailerFooter from './components/RetailerFooter.vue';
import PortalPageHeader from './components/PortalPageHeader.vue';
import { totalCartKits } from '@/utils/cart';

const MAX_CART_KITS = 50;

export default {
  name: 'ShopPage',
  components: { RetailerHeader, RetailerFooter, PortalPageHeader },
  data() {
    return {
      cid: '',
      accessToken: '',
      squareCustomerId: '',
      loading: true,
      error: null,
      pendingApproval: false,
      products: [],
      selectedVariations: {},
      cart: JSON.parse(localStorage.getItem('ucari_cart') || '[]'),
      cartOpen: false,
      maxCartKits: MAX_CART_KITS,
      placingOrder: false,
      orderError: null,
      orderSuccess: null,
      checkoutForm: {
        customerId: '',
        cardId: 'cnon:card-nonce-ok' // Sandbox default
      }
    };
  },
  async mounted() {
    this.accessToken = localStorage.getItem('accessToken');
    this.cid = localStorage.getItem('cid');
    this.squareCustomerId = localStorage.getItem('squareCustomerId');

    if (!this.accessToken || !this.cid) {
      this.$router.replace({ name: 'LoginUser', query: { redirect: '/shop' } });
      return;
    }

    await this.fetchProducts();
    await this.$nextTick();
    window.UIkit?.update();
  }, 
  created() {
    document.title = 'Shop | UCARI';
    this.accessToken = localStorage.getItem('accessToken');
    this.cid = localStorage.getItem('cid');
    this.squareCustomerId = localStorage.getItem('squareCustomerId');
  },
  computed: {
    cartCount() {
      return totalCartKits(this.cart);
    },
    cartSubtotal() {
      return this.cart.reduce((sum, i) => sum + (Number(i.price) * Number(i.quantity)), 0);
    }
  },
  methods: {
    packSizeFromVariationName(name = '') {
      const match = String(name).match(/(\d+)/);
      const count = match ? parseInt(match[1], 10) : 1;
      return Number.isFinite(count) && count > 0 ? count : 1;
    },  
    totalCartKits(cart = this.cart) {
      return cart.reduce((sum, i) => {
        const packSize = Number(i.packSize) || this.packSizeFromVariationName(i.variationName || '');
        const packQty  = Number(i.quantity || 0);
        return sum + (packSize * packQty);
      }, 0);
    },
    totalCartQty(cart = this.cart) {
      return cart.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
    },  
    perKitPrice(v) {
      const cents = v?.priceMoney?.amount;
      if (cents == null) return '-';

      // Try to extract the number of kits from the name, e.g. "2 Pack"
      const name = v?.name || '';
      const match = name.match(/(\d+)/);
      const count = match ? parseInt(match[1], 10) : 1;

      if (!count || isNaN(count)) {
        // Fallback: treat as single kit
        return this.formatMoney(cents);
      }

      const perKitCents = Math.round(Number(cents) / count);
      return this.formatMoney(perKitCents);
    },  
    normalizeProducts(items = []) {
      return items.map((raw) => {
        const d = raw?.item_data || {};
        const vars = Array.isArray(d.variations) ? d.variations : [];

        const flatVariations = vars.map((v) => {
          const vd = v?.item_variation_data || {};
          const pm = vd?.price_money || null;
          return {
            id: v?.id,
            name: vd?.name || v?.itemVariationData?.name || 'Default',
            priceMoney: pm, // { amount, currency }
          };
        });

        return {
          // keep raw in case you ever need it
          _raw: raw,
          id: raw?.id,
          imageUrl: raw?.imageUrl || null,
          name: d?.name || '',
          description: d?.description || '',
          variations: flatVariations,
          selectedVariationId: flatVariations[0]?.id || null,
          qty: 1,
        };
      });
    },  
    async fetchProducts() {
      this.loading = true;
      this.error = null;
      try {
        const url = `${process.env.VUE_APP_API_URL}/api/products`;
        const { data } = await axios.get(url, {
          headers: { 
            Authorization: `Bearer ${this.accessToken}`, 
            cid: this.cid 
          },
          params: {
            customerId: this.squareCustomerId,
          },
        });
        if (!data?.success) throw new Error(data?.error || 'Failed loading products');

        const rawProducts = Array.isArray(data.products) ? data.products : [];
        this.products = this.normalizeProducts(rawProducts);
        this.note = data.note || '';
      } catch (e) {
        const status = e.response?.status || 0;
        const payload = e.response?.data;
        const url = e.config?.url;
        const method = e.config?.method;
        console.error('[Products] request failed', { method, url, status, payload, headers: e.config?.headers });

        if (status === 401 || status === 403) {
          this.$router.replace({ name: 'LoginUser', query: { redirect: '/shop' } });
          return;
        }

        if (status === 500) {
          this.pendingApproval = true;
          return;
        }

        this.error = payload?.error || e.message || 'Unable to load products';
      } finally {
        this.loading = false;
        this.$nextTick(() => window.UIkit?.update?.());
      }
    },
    incrementQty(p) {
      p.qty = Number(p.qty || 1) + 1;
      this.sanitizeQty(p);
    },
    decrementQty(p) {
      if (p.qty > 1) p.qty = Number(p.qty) - 1;
      this.sanitizeQty(p);
    },    
    sanitizeQty(p) {
      let q = parseInt(p.qty, 10);
      if (!Number.isFinite(q) || q < 1) q = 1;

      const v = this.currentVariation(p);
      const packSize = this.packSizeFromVariationName(v?.name || '');

      const currentKits = this.totalCartKits();
      const remainingKits = Math.max(0, MAX_CART_KITS - currentKits);

      // Max packs you can add for THIS product/variation without exceeding kit limit
      const maxPacksAllowed = Math.floor(remainingKits / packSize);

      // If we’re already full, keep input at 1 (or you can set 0 if you allow it)
      if (maxPacksAllowed <= 0) {
        p.qty = 1;
        return;
      }

      if (q > maxPacksAllowed) q = maxPacksAllowed;

      // optional hard cap to keep it reasonable
      if (q > 9999) q = 9999;

      p.qty = q;
    }, 
    formatMoney(cents) {
      if (cents == null) return '-';
      return (Number(cents) / 100).toFixed(2);
    },
    moneyCents(amount) {
      if (typeof amount !== 'number') return '—';
      return `$${(amount / 100).toFixed(2)}`;
    },

    getVariations(item) {
      return item?.item_data?.variations ?? [];
    },

    currentVariation(item) {
      if (!item) return null;

      // Prefer flattened structure your template now uses
      const vars = Array.isArray(item.variations)
        ? item.variations
        : (item?.item_data?.variations || []);

      if (!vars.length) return null;

      const selId =
        item.selectedVariationId ||
        this.selectedVariations?.[item.id];

      return vars.find(v => v?.id === selId) || vars[0] || null;
    },

    displayPrice(item) {
      const v = this.currentVariation(item);
      const pm = v?.priceMoney || v?.item_variation_data?.price_money || v?.itemVariationData?.priceMoney;
      const amount = pm?.amount;
      return amount != null ? (Number(amount) / 100).toFixed(2) : '—';
    },

  persistCart() {
    // write cart to localStorage and notify listeners (Header/App.vue)
    localStorage.setItem('ucari_cart', JSON.stringify(this.cart));
    window.dispatchEvent(new CustomEvent('ucari:cart-updated'));
  },

  openCart() {
    const oc = window.UIkit?.offcanvas?.('#cart-offcanvas');
    oc && oc.show();
  },

  addToCart(p) {
    const v = this.currentVariation(p);
    if (!v) return;

    const packQtyToAdd = Math.max(1, Number(p.qty || 1));
    const packSize = this.packSizeFromVariationName(v.name || '');

    const currentKits = this.totalCartKits();
    const remainingKits = MAX_CART_KITS - currentKits;

    if (remainingKits <= 0) {
      UIkit.notification({ message: `You can only order up to ${MAX_CART_KITS} kits.`, status: 'warning' });
      return;
    }

    // how many packs can we add without exceeding remainingKits?
    const maxPacksAllowed = Math.floor(remainingKits / packSize);

    if (maxPacksAllowed <= 0) {
      UIkit.notification({
        message: `You have room for ${remainingKits} more kits. Please choose a smaller pack size.`,
        status: 'warning'
      });
      return;
    }

    const finalPackQty = Math.min(packQtyToAdd, maxPacksAllowed);

    const priceMoney = v.priceMoney || {};
    const key = `${p.id}:${v.id}`;
    const existing = this.cart.find(ci => ci.key === key);

    if (existing) {
      existing.quantity += finalPackQty;
    } else {
      this.cart.push({
        key,
        productId: p.id,
        productName: p.name || 'Product',
        variationId: v.id,
        variationName: v.name || 'Default',
        packSize,
        price: Number(priceMoney.amount || 0),
        currency: priceMoney.currency || 'USD',
        quantity: finalPackQty
      });
    }

    if (finalPackQty < packQtyToAdd) {
      UIkit.notification({
        message: `Only ${finalPackQty} added (${MAX_CART_KITS} kit limit).`,
        status: 'primary'
      });
    }

    this.persistCart();
    this.openCart();

    axios.post(`${process.env.VUE_APP_API_URL}/api/retailer/add-to-cart`, {
      productId: p.id,
      productName: p.name,
      variationId: v.id,
      variationName: v.name,
      quantity: finalPackQty,                 // packs
      unitPriceCents: Number(priceMoney.amount || 0)
    }, {
      headers: { Authorization: `Bearer ${this.accessToken}`, cid: this.cid }
    }).catch(() => {});
  },
  imageUrl(item) {
    // server adds imageUrl; fall back to first image id mapping if present, else placeholder
    return item?.imageUrl || '/images/product-placeholder.png';
  },
  }
};
</script>
